import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Wallet, TrendingUp, Percent, HandCoins, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/form/Select';
import { Input } from '@/components/form/Input';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { formatINR, formatDate } from '@/lib/format';
import { downloadExcel } from '@/lib/downloadExcel';
import { listSisStores } from '@/api/sisStock';
import { listSisCreditNoteLines, sisCreditNoteLinesExportPath, type SisCnLine } from '@/api/sisCreditNote';

export function SisProfitabilityPage() {
  const [partyId, setPartyId] = useState('');
  const [group, setGroup] = useState('');
  // Sale date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  const [search, setSearch] = useState('');

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });

  const groupOptions = useMemo(
    () => Array.from(new Set((storesQ.data ?? []).map((s) => s.group).filter(Boolean))).sort() as string[],
    [storesQ.data],
  );
  const storeOptions = useMemo<SearchableOption[]>(
    () => [
      { value: '', label: 'All Stores' },
      ...(storesQ.data ?? []).map((s) => ({ value: String(s.id), label: s.name, sublabel: s.city || undefined })),
    ],
    [storesQ.data],
  );

  const filter = useMemo(
    () => ({ partyId: partyId ? Number(partyId) : undefined, group: group || undefined, from: range?.from, to: range?.to }),
    [partyId, group, range],
  );
  const linesQ = useQuery({
    queryKey: ['sis-profitability', filter],
    queryFn: () => listSisCreditNoteLines(filter),
  });
  const lines = linesQ.data?.lines ?? [];
  const t = linesQ.data?.totals;
  const blendedPct = t && t.itemCost > 0 ? Math.round((t.netProfit / t.itemCost) * 1000) / 10 : 0;

  // Free-text search across EVERY column of the loaded lines (all terms must match).
  const filteredLines = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lines;
    const terms = q.split(/\s+/);
    return lines.filter((r) => {
      const hay = [
        formatDate(r.saleDate), r.storeName, r.group, r.txnId, r.invoiceNo,
        r.itemName, r.itemCode, r.barcode, r.units, r.mrpAmt, r.discount,
        r.sellingGross, r.sellingNet, r.sellingGst, r.commission, r.commissionPct,
        r.payableInclGst, r.itemCost, r.netProfitPerPc, r.netProfit, r.netProfitPct,
      ].map((v) => (v == null ? '' : String(v))).join(' ').toLowerCase();
      return terms.every((term) => hay.includes(term));
    });
  }, [lines, search]);

  const searching = search.trim().length > 0;
  const shownPayable = searching ? filteredLines.reduce((a, r) => a + (r.payableInclGst || 0), 0) : (t?.payableInclGst ?? 0);
  const shownNet = searching ? filteredLines.reduce((a, r) => a + (r.netProfitPerPc || 0) * (r.units || 0), 0) : (t?.netProfit ?? 0);

  return (
    <div>
      <SectionHeader
        title="SIS Profitability"      />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Amt Received (incl GST)" value={`₹${formatINR(t?.payableInclGst ?? 0)}`} accent="#6366f1" Icon={HandCoins} />
        <KpiCard label="Net Profit (ex-GST)" value={`₹${formatINR(t?.netProfit ?? 0)}`} accent="#10b981" Icon={TrendingUp} />
        <KpiCard label="Net Profit %" value={`${blendedPct}%`} accent="#8b5cf6" Icon={Percent} />
        <KpiCard label="Commission to Stores" value={`₹${formatINR(t?.commission ?? 0)}`} accent="#f59e0b" Icon={Wallet} />
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-3 sm:col-span-1 sm:min-w-[220px]">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              title="Search any column"
              className="!pl-8"
            />
          </div>
        </FilterField>
        <FilterField label="Group" className="sm:min-w-[150px]">
          <Select value={group} onChange={(e) => { setGroup(e.target.value); setPartyId(''); }}>
            <option value="">All Groups</option>
            {groupOptions.map((g) => <option key={g}>{g}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Store" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <SearchableSelect
            options={storeOptions.filter((o) => o.value === '' || !group || (storesQ.data ?? []).find((s) => String(s.id) === o.value)?.group === group)}
            value={partyId} onChange={(v) => setPartyId(v === '' ? '' : String(v))} placeholder="Type a store name…" />
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <div className="col-span-3 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button
            variant="glass"
            size="sm"
            icon={<Download size={12} />}
            onClick={() => downloadExcel(sisCreditNoteLinesExportPath(filter), `GRHAMOY_SIS_Profitability_${group || (partyId ? 'Store' : 'All')}.xlsx`, 120000)}
            className="w-full justify-center sm:w-auto"
            title="Export the Profitability lines for the current group / store / period (no cap)"
          >
            Export
          </Button>
        </div>
      </FilterBar>

      <Table<SisCnLine>
        storageKey="sis-profitability"
        paginated
        title={`Profitability — ${group || 'All Groups'}`}
        count={`${filteredLines.length}${searching ? ` of ${lines.length}` : ''} lines · Payable ₹${formatINR(shownPayable)} · Net profit ₹${formatINR(shownNet)}`}
        rows={filteredLines}
        rowKey={(r) => `${r.partyId}|${r.txnId}|${r.invoiceNo}|${r.itemCode}`}
        loading={linesQ.isFetching && lines.length === 0}
        empty={searching && lines.length > 0 ? 'No lines match your search.' : 'No sale lines for this filter — upload store sales reports (SIS Sales Report) to populate profitability.'}
        columns={[
          { key: 'date', header: 'Date', minWidth: '100px', cell: (r) => formatDate(r.saleDate) },
          { key: 'store', header: 'Store', minWidth: '160px', cell: (r) => <strong>{r.storeName}</strong> },
          { key: 'group', header: 'Group', minWidth: '100px', cell: (r) => r.group || '—' },
          { key: 'bill', header: 'Bill No', minWidth: '110px', cell: (r) => r.txnId },
          { key: 'inv', header: 'Src Invoice', minWidth: '120px', cell: (r) => r.invoiceNo },
          { key: 'item', header: 'Item', minWidth: '180px', cell: (r) => <span className="font-bold">{r.itemName}</span> },
          { key: 'barcode', header: 'Barcode', minWidth: '110px', cell: (r) => r.barcode ? <span className="font-mono text-[var(--text-secondary)]">{r.barcode}</span> : '—' },
          { key: 'qty', header: 'Qty', align: 'right', cell: (r) => r.units },
          { key: 'mrp', header: 'MRP', align: 'right', minWidth: '100px', cell: (r) => `₹${formatINR(r.mrpAmt)}` },
          { key: 'disc', header: 'Discount %', align: 'right', minWidth: '90px', cell: (r) => (r.mrpAmt > 0 && r.discount ? <span className="text-[var(--text-secondary)]">{(Math.round((r.discount / r.mrpAmt) * 1000) / 10)}%</span> : '—') },
          { key: 'sellGross', header: 'Store Selling Amt (Gross)', align: 'right', minWidth: '130px', cell: (r) => `₹${formatINR(r.sellingGross)}` },
          { key: 'sellNet', header: 'Store Selling Amt (Net)', align: 'right', minWidth: '130px', cell: (r) => `₹${formatINR(r.sellingNet)}` },
          { key: 'sellGst', header: 'Store Selling GST', align: 'right', minWidth: '110px', cell: (r) => `₹${formatINR(r.sellingGst)}` },
          { key: 'comm', header: 'Commission', align: 'right', minWidth: '110px', cell: (r) => <span>₹{formatINR(r.commission)} <span className="text-[10px] text-[var(--text-muted)]">({r.commissionPct}%)</span></span> },
          { key: 'payable', header: 'Amt Received (incl GST)', align: 'right', minWidth: '150px', cell: (r) => <strong className="text-accent">₹{formatINR(r.payableInclGst)}</strong> },
          { key: 'itemCost', header: 'Cost of the Item (Item Master)', align: 'right', minWidth: '150px', cell: (r) => `₹${formatINR(r.itemCost)}` },
          { key: 'profit', header: 'Net Profit/Pc (Exc. GST)', align: 'right', minWidth: '130px', cell: (r) => <span className={r.netProfitPerPc >= 0 ? 'text-success' : 'text-danger'}>₹{formatINR(r.netProfitPerPc)}</span> },
          { key: 'netProfit', header: 'Net Profit (Exc. GST)', align: 'right', minWidth: '130px', cell: (r) => <strong className={r.netProfit >= 0 ? 'text-success' : 'text-danger'}>₹{formatINR(r.netProfit)}</strong> },
          { key: 'profitPct', header: 'Net Profit %', align: 'right', minWidth: '90px', cell: (r) => <strong className={r.netProfitPct >= 0 ? 'text-success' : 'text-danger'}>{r.netProfitPct.toFixed(1)}%</strong> },
        ]}
      />
    </div>
  );
}
